// The editor for a Hyperlink custom field on the record page, the timesheet, the scoping table and the
// ticket panel. A saved link is the value itself, opening in a new tab, with an edit button beside it; an
// empty field offers "Add link". Editing is its own step with Save and Cancel (Enter saves, Escape
// cancels). A link without a scheme is saved with https:// added. An invalid link is not saved: the reason
// shows under the box and the user stays in editing. The box has no maxLength, because the browser would
// cut a pasted long link short; a long link gets the length message instead. No click reaches the row or
// cell behind, so editing in a table row does not also select or expand it.
//
// By default the editing step replaces the link in place. With `editInPopover` it opens in a popover
// anchored to the Add link or Edit button instead, for table cells whose rows have a fixed height and
// would clip the box and its buttons. With `boxOnly` the editing step is the box alone, for a narrow table
// cell such as the timesheet's: Enter saves, Escape cancels, and leaving the box saves a valid link (an
// invalid one keeps the box open with its reason).
import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {Button, PopoverHandler, TextField, tokensForThemeProvider} from '@forecast-it/design-system';
import {
	HYPERLINK_MAX_LENGTH,
	HYPERLINK_PROBLEM_MESSAGE_ID,
	normalizeHyperlink,
} from '../forecast-app/shared/util/customFieldValueValidation';
import {CustomFieldHyperlink} from './CustomFieldHyperlink';

const Row = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	min-width: 0;
`;

// Lets the link shrink, so a long one ends in an ellipsis instead of pushing the edit button out.
const LinkBox = Styled.div`
	flex: 1;
	min-width: 0;
	overflow: hidden;
`;

const Editing = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
`;

// The box alone in a table row: padded to fill the row's 48px band exactly with the 30px box centred in
// it. The row does not grow: the reason for an invalid link opens under the box, over the row below, on
// the page's background so it reads clearly. It sits above the timesheet's sticky total row (z-index 1),
// which would otherwise cover the reason on the last row.
const BoxOnlyEditing = Styled(Editing)`
	align-self: flex-start;
	position: relative;
	z-index: 2;
	padding: 10px 0 9px;
	box-sizing: border-box;
	background-color: ${tokensForThemeProvider.sky.sys.color.background.neutral['1']};
`;

// A fixed width, so a long link scrolls inside the box and the error wraps under it instead of
// stretching the popover across the table.
const PopoverEditing = Styled(Editing)`
	width: 280px;
	padding: 12px;
	box-sizing: border-box;
`;

type CustomFieldHyperlinkInputProps = {
	value: string | null | undefined;
	onSave: (value: string | null) => void;
	readOnly?: boolean;
	// Turns off saving and starting an edit, such as while another save is in flight. An edit that is
	// already open stays open with what the user typed.
	disabled?: boolean;
	// The field's display name: names the box and the buttons for screen readers.
	label: string;
	// Edit in a popover anchored to the button, for a table cell that cannot grow to fit the editor.
	editInPopover?: boolean;
	// Edit with the box alone, without Save and Cancel, for a narrow table cell.
	boxOnly?: boolean;
	className?: string;
	'data-cy'?: string;
};

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

type HyperlinkEditorProps = {
	// The link as stored; the box starts from it each time the editor opens.
	stored: string;
	label: string;
	dataCy: string;
	// Called with the normalised link (null clears it), only when it differs from the stored one.
	onCommit: (value: string | null) => void;
	// Ends the edit, after a save as well as on Cancel or Escape.
	onClose: () => void;
	inPopover?: boolean;
	// The box alone, without Save and Cancel; leaving the box saves.
	boxOnly?: boolean;
	// Nothing is saved while set; the box keeps the draft.
	disabled?: boolean;
	className?: string;
};

// The editing step: the box with the error under it, then Save and Cancel (or the box alone). Every mode
// renders this, and it is mounted afresh each time editing starts, so the draft and the error always start
// from the stored link.
const HyperlinkEditor = ({
	stored,
	label,
	dataCy,
	onCommit,
	onClose,
	inPopover,
	boxOnly,
	disabled,
	className,
}: HyperlinkEditorProps): React.JSX.Element => {
	const {formatMessage} = useIntl();
	const [draft, setDraft] = useState(stored);
	const [error, setError] = useState<string | null>(null);
	const editingRef = useRef<HTMLDivElement>(null);
	// Set once the edit has ended, so the blur that follows Escape or Enter does not save a second time.
	const closed = useRef(false);
	const close = () => {
		closed.current = true;
		onClose();
	};

	// The design-system TextField has no autofocus prop, so the box is focused through its wrapper.
	useEffect(() => {
		editingRef.current?.querySelector('input')?.focus();
	}, []);

	const save = () => {
		if (disabled) return;
		const result = normalizeHyperlink(draft);
		if (!result.valid) {
			setError(formatMessage({id: HYPERLINK_PROBLEM_MESSAGE_ID[result.reason]}, {maxLength: HYPERLINK_MAX_LENGTH}));
			return;
		}
		// Saving the link that is already stored would commit a no-op mutation.
		if ((result.value ?? '') !== stored) onCommit(result.value);
		close();
	};

	const Container = inPopover ? PopoverEditing : boxOnly ? BoxOnlyEditing : Editing;
	return (
		<Container
			ref={editingRef}
			className={className}
			onClick={stop}
			onBlur={
				boxOnly
					? (e: React.FocusEvent) => {
							// Focus moving within the editor is not leaving it.
							if (closed.current || e.currentTarget.contains(e.relatedTarget as Node | null)) return;
							save();
					  }
					: undefined
			}
			onKeyDown={(e: React.KeyboardEvent) => {
				// No key typed in the box reaches a row-level key handler behind it.
				e.stopPropagation();
				// Only the box's own Enter saves: Enter on the Save or Cancel button already presses it.
				if (e.key === 'Enter' && e.target instanceof HTMLInputElement && !e.nativeEvent.isComposing) {
					e.preventDefault();
					save();
				} else if (e.key === 'Escape') {
					// Keeps Escape from reaching listeners further up the React tree and on the window, such as the
					// generic modal conductor's, so it cancels the edit without closing the modal. A listener that
					// runs in the capture phase on the document is not stopped by this.
					close();
				}
			}}
		>
			<TextField
				aria-label={label}
				data-cy={dataCy}
				size="small"
				width="flex"
				value={draft}
				onValueChange={(next: string) => {
					setDraft(next);
					setError(null);
				}}
				hasError={!!error}
				errorText={error ?? undefined}
			/>
			{boxOnly ? null : (
				<Row>
					<Button emphasis="medium" data-cy={`${dataCy}-save`} disabled={disabled} onPress={save}>
						{formatMessage({id: 'common.save'})}
					</Button>
					<Button emphasis="low" data-cy={`${dataCy}-cancel`} onPress={close}>
						{formatMessage({id: 'common.cancel'})}
					</Button>
				</Row>
			)}
		</Container>
	);
};

type PopoverPartProps = {
	stored: string;
	label: string;
	dataCy: string;
	onCommit: (value: string | null) => void;
	disabled?: boolean;
};

// The Add link or Edit button. The design-system Button drops `onClick`, so the click handler that
// PopoverHandler.Trigger merges onto it never runs; pressing it opens the popover through its state
// instead. The trigger still gives it its ref (the popover's anchor, and where focus returns on close)
// and its aria-expanded.
const PopoverActionButton = ({stored, label, dataCy, disabled}: Omit<PopoverPartProps, 'onCommit'>): React.JSX.Element => {
	const {formatMessage} = useIntl();
	const {setOpen} = PopoverHandler.usePopoverState();
	// One Button for both states, so the element that has focus stays the same when a first link is saved
	// and Add link becomes Edit.
	return (
		<PopoverHandler.Trigger>
			<Button
				iconOnly={!!stored}
				emphasis="low"
				icon={stored ? 'edit' : 'plus'}
				disabled={disabled}
				data-cy={stored ? `${dataCy}-edit` : `${dataCy}-add`}
				aria-label={formatMessage(
					{id: stored ? 'custom_field_value.edit_link' : 'custom_field_value.add_link_to'},
					{field: label}
				)}
				onPress={() => setOpen(open => !open)}
			>
				{stored ? undefined : formatMessage({id: 'custom_field_value.add_link'})}
			</Button>
		</PopoverHandler.Trigger>
	);
};

// Runs inside the popover, where its open state is available to close it. Radix unmounts it whenever the
// popover closes (Save, Cancel, Escape or a press outside), so the next opening starts from the stored
// link. Radix also moves focus into it on open and back to the button on close, except after a press
// outside, where focus stays where the user put it.
const PopoverEditorHost = ({stored, label, dataCy, onCommit, disabled}: PopoverPartProps): React.JSX.Element => {
	const {setOpen} = PopoverHandler.usePopoverState();
	return (
		<HyperlinkEditor
			inPopover
			disabled={disabled}
			stored={stored}
			label={label}
			dataCy={dataCy}
			onCommit={onCommit}
			onClose={() => setOpen(false)}
		/>
	);
};

export const CustomFieldHyperlinkInput = ({
	value,
	onSave,
	readOnly,
	disabled = false,
	label,
	editInPopover = false,
	boxOnly = false,
	className,
	'data-cy': dataCy = 'custom-field-hyperlink-input',
}: CustomFieldHyperlinkInputProps): React.JSX.Element | null => {
	const {formatMessage} = useIntl();
	const stored = value ?? '';
	const [editing, setEditing] = useState(false);
	const actionRef = useRef<HTMLButtonElement>(null);
	// Set when an edit ends, so focus goes back to the button that started it, and not on first mount.
	const returnFocus = useRef(false);
	// The value a save is waiting to see come back, so focus lands on the button that replaces the old one.
	const awaitedValue = useRef<string | null>(null);

	// Once the edit box is gone, the Edit or Add link button takes focus back.
	useEffect(() => {
		if (!editing && returnFocus.current && (awaitedValue.current === null || awaitedValue.current === stored)) {
			returnFocus.current = false;
			awaitedValue.current = null;
			actionRef.current?.focus();
		}
	}, [editing, stored]);

	if (readOnly) return value ? <CustomFieldHyperlink value={value} /> : null;

	if (editInPopover) {
		return (
			<Row className={className} onClick={stop}>
				{stored ? (
					<LinkBox>
						<CustomFieldHyperlink value={value} />
					</LinkBox>
				) : null}
				<PopoverHandler>
					<PopoverActionButton stored={stored} label={label} dataCy={dataCy} disabled={disabled} />
					<PopoverHandler.Content align="center">
						<PopoverEditorHost
							stored={stored}
							label={label}
							dataCy={dataCy}
							onCommit={onSave}
							disabled={disabled}
						/>
					</PopoverHandler.Content>
				</PopoverHandler>
			</Row>
		);
	}

	const startEditing = () => {
		returnFocus.current = false;
		awaitedValue.current = null;
		setEditing(true);
	};

	if (editing) {
		return (
			<HyperlinkEditor
				className={className}
				boxOnly={boxOnly}
				disabled={disabled}
				stored={stored}
				label={label}
				dataCy={dataCy}
				onCommit={next => {
					awaitedValue.current = next ?? '';
					onSave(next);
				}}
				onClose={() => {
					returnFocus.current = true;
					setEditing(false);
				}}
			/>
		);
	}

	if (!stored) {
		return (
			<Row className={className} onClick={stop}>
				<Button
					ref={actionRef}
					emphasis="low"
					icon="plus"
					disabled={disabled}
					data-cy={`${dataCy}-add`}
					aria-label={formatMessage({id: 'custom_field_value.add_link_to'}, {field: label})}
					onPress={startEditing}
				>
					{formatMessage({id: 'custom_field_value.add_link'})}
				</Button>
			</Row>
		);
	}

	return (
		<Row className={className} onClick={stop}>
			<LinkBox>
				<CustomFieldHyperlink value={value} />
			</LinkBox>
			<Button
				ref={actionRef}
				iconOnly
				emphasis="low"
				icon="edit"
				disabled={disabled}
				data-cy={`${dataCy}-edit`}
				aria-label={formatMessage({id: 'custom_field_value.edit_link'}, {field: label})}
				onPress={startEditing}
			/>
		</Row>
	);
};

export default CustomFieldHyperlinkInput;
